import React from "react";
import { useTheme } from "@mui/material/styles";
import { green } from "@mui/material/colors";
import MobileAppBar from "./MobileAppBar";
import { Typography, Box } from "@mui/material";
import { useUserControls } from "../../context/UserControlsProvider";
import useAppBarMenus from "./useAppBarMenus";
import { useOfflineSync } from "../../context/OfflineSyncProvider";
import Chip from "@mui/material/Chip";
import { formatStatusLabel } from "../../utils/status";
import { isVerboseLoggingEnabled } from "../../utils/debug";

type MenuBundle = { menuItems: { key?: string | number; label: React.ReactNode; onClick?: () => void }[]; drawers: React.ReactNode };

const ShipmentViewAppBar: React.FC<{ renderDrawers?: boolean; sourceTag?: string; menusOverride?: MenuBundle | null; position?: 'fixed' | 'static' }> = ({ renderDrawers = true, sourceTag = "ShipmentViewAppBar", menusOverride = null, position = 'fixed' }) => {
  const {
    selectedShipment,
    processedGroups,
    unprocessedGroups,
    selectedShipmentObject,
    loadingStatus,
    unloadLocation,
    actionStatus,
  } = useUserControls();
  const { isOnline, isForcedOffline } = useOfflineSync();
  const theme = useTheme();
  const verbose = isVerboseLoggingEnabled();

  const processedCount = Object.values(processedGroups).flat().length;
  const unprocessedCount = Object.values(unprocessedGroups).flat().length;
  const itemCount = processedCount + unprocessedCount;

  const titleText = `Shipment: ${selectedShipment}`;
  const receivedLine = (() => {
    if (processedCount === itemCount) {
      return loadingStatus === "loading"
        ? `All ${processedCount} Items Loaded`
        : `All ${processedCount} Items Received`;
    }
    return loadingStatus === "loading"
      ? `Loaded ${processedCount}/${itemCount} Items`
      : `Received ${processedCount}/${itemCount} Items`;
  })();
  const locationLine = `Location: ${unloadLocation || "Select Location"}`;
  const actionLabel = (() => {
    const formatted = formatStatusLabel(actionStatus);
    return formatted || undefined;
  })();
  const receivedWithAction = actionLabel
    ? `${receivedLine} • Action: ${actionLabel}`
    : receivedLine;
  const subtitleText = `${locationLine}\n${receivedWithAction}`;

  const fg = (theme as any)?.palette?.primary?.contrastText ?? "#fff";
  const bg = (() => {
    if (processedCount === itemCount && !!selectedShipmentObject) {
      return green[800];
    }
    return (theme as any)?.brand ?? theme.palette.primary.main;
  })();

  // use shared hook for menu items and drawers
  const {
    menuItems: sharedMenuItems,
    drawers,
    shouldPromptSelection,
  } = useAppBarMenus();
  React.useEffect(() => {
    if (!verbose) return;
    console.log(`[${sourceTag}] using useAppBarMenus`, { renderDrawers });
  }, [sourceTag, renderDrawers, verbose]);
  React.useEffect(() => {
    if (!verbose) return;
    console.log("[ShipmentViewAppBar] Prompt selection status", {
      shouldPromptSelection,
      selectedShipment,
      loadingStatus,
      processedCount,
      unprocessedCount,
    });
  }, [shouldPromptSelection, selectedShipment, loadingStatus, processedCount, unprocessedCount, verbose]);

  const [expandLocation, setExpandLocation] = React.useState(false);
  const offlineChip = isForcedOffline || !isOnline ? (
    <Chip
      label="Offline"
      color="error"
      size="small"
      sx={{
        ml: { xs: 0.5, sm: 1 },
        backgroundColor: 'rgba(211, 47, 47, 0.65)',
        color: '#fff',
        fontWeight: 600,
        letterSpacing: 0.4,
      }}
    />
  ) : null;

  const subtitleNode = (
    <>
      <Typography
        variant="subtitle1"
        onClick={() => setExpandLocation((v) => !v)}
        sx={{
          cursor: "pointer",
          fontWeight: 500,
          color: "inherit",
          whiteSpace: expandLocation ? "normal" : "nowrap",
          overflow: expandLocation ? "visible" : "hidden",
          textOverflow: expandLocation ? "clip" : "ellipsis",
        }}
        title={locationLine}
      >
        {locationLine}
      </Typography>
      <Typography
        variant="subtitle1"
        sx={{
          fontWeight: 500,
          color: "inherit",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
        title={receivedWithAction}
      >
        {receivedWithAction}
      </Typography>
    </>
  );

  return (
    <>
      {verbose && console.log('[ShipmentViewAppBar] render', { sourceTag, renderDrawers, processedCount, unprocessedCount, selectedShipment })}
      <MobileAppBar
        title={titleText}
        subtitle={subtitleText}
        subtitleNode={subtitleNode}
        backgroundColor={bg}
        textColor={fg}
        elevation={0}
        position={position}
        menuItems={menusOverride ? menusOverride.menuItems : sharedMenuItems}
        rightChip={offlineChip}
      />

      {renderDrawers ? (
        <>
          {verbose && console.log(`[${sourceTag}] rendering drawers`)}
          {menusOverride ? menusOverride.drawers : drawers}
        </>
      ) : null}
    </>
  );
};

export default ShipmentViewAppBar;
